import { Component, computed, inject, input, model, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT, ModalService, TranslateLoader, UserService } from '@cfp/core';
import { PortfolioPdfDownloadButton } from '@cfp/domains/portfolio/pdf-download-button';
import { AiButton } from '@cfp/features-shared/ai';
import { NotesButton } from '@cfp/features-shared/notes';
import { ReportBuildingsDownloadButton } from '@cfp/features-shared/portfolio-buildings-roadmap-report';
import { TagsPortfolioModal } from '@cfp/features-shared/tags';
import { LabelGroupModule } from '@cfp/shared/ui/label-group';
import { MeasurementArea } from '@cfp/shared/ui/measurement';
import {
  Badge,
  Button,
  ButtonToggle,
  CfpTranslateModule,
  Dropdown,
  Icon,
  LiveRegionStatusUpdaterModule
} from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { LabelGroupDTO, PortfolioItemDTO } from 'csr-asset-app';
import { catchError, map, of } from 'rxjs';

@Component({
  selector: 'cfp-portfolio-header',
  templateUrl: './portfolio-header.html',
  styleUrls: ['./portfolio-header.scss'],
  imports: [
    ReportBuildingsDownloadButton,
    LabelGroupModule,
    TranslateModule,
    Button,
    ButtonToggle,
    Icon,
    LiveRegionStatusUpdaterModule,
    MeasurementArea,
    PortfolioPdfDownloadButton,
    AiButton,
    NotesButton,
    Badge,
    Dropdown,
    CfpTranslateModule
  ]
})
export class PortfolioHeader {
  public readonly portfolio = input.required<PortfolioItemDTO>();
  public readonly portfolioId = computed(() => this.portfolio()?.id ?? 0);
  public readonly showLabelGroup = input<boolean>(true);
  public readonly activeFiltersCount = input<number>(0);
  public readonly labelGroups = input<LabelGroupDTO[]>([]);
  public readonly labelGroup = input<LabelGroupDTO | null>();
  public readonly filtersButtonDisabled = input<boolean>(false);

  public readonly showM2 = model<boolean>(false);

  public readonly toggleM2 = output<void>();
  public readonly showFiltersSidePanel = output<void>();
  public readonly showAddBuildingFlow = output<boolean>();
  public readonly clearFilters = output<MouseEvent>();

  protected readonly environment = inject(ENVIRONMENT);
  protected readonly userService = inject(UserService);
  protected readonly translateLoader = inject(TranslateLoader);
  private readonly modalService = inject(ModalService);

  protected readonly hasPortfolioBuildingsRoadmapReportLicense = toSignal(
    this.userService.hasLicense('portfolio_buildings_roadmap_report'),
    { initialValue: false }
  );
  protected readonly hasAILicense = toSignal(this.userService.hasLicense('ai'), { initialValue: false });
  protected readonly hasPortfolioReportLicense = toSignal(this.userService.hasLicense('portfolio_report'), {
    initialValue: false
  });
  protected readonly hasCrremLicense = toSignal(this.userService.hasLicense('crrem'), { initialValue: false });
  protected readonly hasRoadmapLicense = toSignal(this.userService.hasLicense('roadmap'), { initialValue: false });
  protected readonly hasPortfolioNotesLicense = toSignal(this.userService.hasLicense('portfolio_notes'), {
    initialValue: false
  });

  protected readonly hideManageBuildingTags = this.environment.components.hideManageBuildingTags === true;

  protected readonly hasAiTranslationLoaded = toSignal(
    this.translateLoader.loadTranslation('ai').pipe(
      map(() => true),
      catchError(() => of(false))
    ),
    {
      initialValue: false
    }
  );

  protected readonly reportUrl = computed(() => `/report/${this.portfolioId()}`);
  protected readonly reportFileName = computed(() => `${this.portfolio().name}-portfolio-report.pdf`);

  protected readonly url = computed(
    () => `/report/${this.portfolioId()}${this.hasPortfolioBuildingsRoadmapReportLicense() ? '/buildings' : ''}`
  );

  protected showTags(): void {
    this.modalService.showSidePanel(TagsPortfolioModal, {
      initialState: {
        portfolioId: this.portfolioId()
      }
    });
  }
}
